import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { DiscountCampaignAnalyticsShape } from "../../types/discount-analytics-types";

type SortKey =
  | "name"
  | "redemptions"
  | "uniqueCustomers"
  | "amount"
  | "revenue"
  | "newCustomers"
  | "repeatRate";

interface Props {
  campaigns: DiscountCampaignAnalyticsShape[];
  selectedDiscountId: string | null;
  onSelect: (discountId: string) => void;
}

function couponTypeLabel(t: (k: string) => string, type?: string | null): string {
  if (type === "PUBLIC") return t("discounts.table.public");
  if (type === "PRIVATE") return t("discounts.table.private");
  return t("discounts.table.auto");
}

export function DiscountCampaignTable({ campaigns, selectedDiscountId, onSelect }: Props) {
  const { t } = useTranslation("reports");
  const [sortKey, setSortKey] = useState<SortKey>("amount");
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = useMemo(() => {
    const arr = [...campaigns];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "name":
          cmp = a.discountName.localeCompare(b.discountName);
          break;
        case "redemptions":
          cmp = a.redemptionCount - b.redemptionCount;
          break;
        case "uniqueCustomers":
          cmp = a.uniqueCustomers - b.uniqueCustomers;
          break;
        case "amount":
          cmp = a.totalAmountDiscounted - b.totalAmountDiscounted;
          break;
        case "revenue":
          cmp = a.totalDiscountedRevenue - b.totalDiscountedRevenue;
          break;
        case "newCustomers":
          cmp = a.newCustomerCount - b.newCustomerCount;
          break;
        case "repeatRate":
          cmp = (a.repeatVisitRate30d ?? -1) - (b.repeatVisitRate30d ?? -1);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [campaigns, sortKey, sortAsc]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const formatPercent = (v: number | null | undefined) =>
    v == null ? "—" : `${(v * 100).toFixed(0)}%`;

  return (
    <Card className="overflow-hidden border-rose-200/70">
      <CardHeader className="border-b border-rose-100 bg-gradient-to-r from-rose-50 via-pink-50/60 to-rose-50 py-3">
        <CardTitle className="text-sm font-semibold text-rose-900">
          {t("discounts.table.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-rose-50/80 hover:bg-rose-50/80 border-b border-rose-100">
              <SortableHead
                label={t("discounts.table.campaignName")}
                active={sortKey === "name"}
                asc={sortAsc}
                onClick={() => handleSort("name")}
              />
              <TableHead className="text-center text-xs font-semibold uppercase tracking-wide text-rose-800">
                {t("discounts.table.type")}
              </TableHead>
              <TableHead className="text-center text-xs font-semibold uppercase tracking-wide text-rose-800">
                {t("discounts.table.status")}
              </TableHead>
              <SortableHead
                label={t("discounts.table.redemptions")}
                active={sortKey === "redemptions"}
                asc={sortAsc}
                onClick={() => handleSort("redemptions")}
              />
              <SortableHead
                label={t("discounts.table.uniqueCustomers")}
                active={sortKey === "uniqueCustomers"}
                asc={sortAsc}
                onClick={() => handleSort("uniqueCustomers")}
              />
              <SortableHead
                label={t("discounts.table.amountDiscounted")}
                active={sortKey === "amount"}
                asc={sortAsc}
                onClick={() => handleSort("amount")}
              />
              <SortableHead
                label={t("discounts.table.discountedRevenue")}
                active={sortKey === "revenue"}
                asc={sortAsc}
                onClick={() => handleSort("revenue")}
              />
              <SortableHead
                label={t("discounts.table.newCustomers")}
                active={sortKey === "newCustomers"}
                asc={sortAsc}
                onClick={() => handleSort("newCustomers")}
              />
              <SortableHead
                label={t("discounts.table.repeatRate")}
                active={sortKey === "repeatRate"}
                asc={sortAsc}
                onClick={() => handleSort("repeatRate")}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => {
              const isSelected = selectedDiscountId === c.discountId;
              return (
                <TableRow
                  key={c.discountId}
                  onClick={() => onSelect(c.discountId)}
                  className={cn(
                    "cursor-pointer border-b-2 border-slate-200 transition-colors",
                    !isSelected && "bg-white hover:bg-rose-50",
                    isSelected && "bg-rose-100/80 hover:bg-rose-100 ring-2 ring-inset ring-rose-300"
                  )}
                >
                  <TableCell
                    className={cn(
                      "text-center font-semibold py-3 text-slate-900",
                      isSelected && "text-rose-900"
                    )}
                  >
                    {c.discountName}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className="border-rose-200 bg-rose-50 text-rose-700 font-medium"
                    >
                      {couponTypeLabel(t, c.couponType)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {c.isActive ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">
                        {t("discounts.table.active")}
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="bg-slate-100 text-slate-600 border border-slate-200"
                      >
                        {t("discounts.table.inactive")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center tabular-nums font-medium text-slate-800">
                    {c.redemptionCount}
                  </TableCell>
                  <TableCell className="text-center tabular-nums font-medium text-slate-800">
                    {c.uniqueCustomers}
                  </TableCell>
                  <TableCell className="text-center tabular-nums font-semibold text-rose-700">
                    <PriceDisplay amount={c.totalAmountDiscounted} />
                  </TableCell>
                  <TableCell className="text-center tabular-nums font-semibold text-emerald-700">
                    <PriceDisplay amount={c.totalDiscountedRevenue} />
                  </TableCell>
                  <TableCell className="text-center tabular-nums text-slate-700">
                    {c.newCustomerCount}
                  </TableCell>
                  <TableCell className="text-center tabular-nums text-slate-700">
                    {formatPercent(c.repeatVisitRate30d)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SortableHead({
  label,
  active,
  asc,
  onClick,
}: {
  label: string;
  active: boolean;
  asc: boolean;
  onClick: () => void;
}) {
  return (
    <TableHead className="text-center">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition-colors",
          active ? "text-rose-900" : "text-rose-800 hover:text-rose-900"
        )}
      >
        {label}
        {active ? <span className="text-[10px] text-rose-600">{asc ? "▲" : "▼"}</span> : null}
      </button>
    </TableHead>
  );
}
